/* eslint-disable max-statements */
import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC, lazy, useContext } from 'react';

import BodyRender from '~/components/BodyRender';
import { BodyRenderExternalData } from '~/components/BodyRender/models';
import { ItemListingCarouselProps } from '~/components/ItemListing/models';
import LoginRegisterBlocks from '~/components/LoginRegisterBlocks';
import { PAGE_ONLY_FOR_SIGNED_IN } from '~/components/LoginRegisterBlocks/constants';
import { mapContentstackTranslations } from '~/context/TranslationContext/mapper';
import { useAuthGuard } from '~/hooks/useAuthGuard';
import { Layout } from '~/layout/Layout';
import { itemListingMapper } from '~/mappers/itemListing';
import {
  createItemListingCarouselMock,
  mapPageDataToCarouselItem,
  PAGE_TYPE,
} from '~/mocks/itemListingCaorusel';
import { addBrandPostfixConditionally } from '~/utils/addBrandPostfixConditionally';
import withContentstackPreview from '~/utils/livePreview';
import { ConsumptionType, getConsumptionTypeLabel } from '~/utils/translation';

import { ResourcePageData } from './models';

const ItemListing = lazy(() => import('~/components/ItemListing'));

const BASE_CLASS = 'l2-content-page';

export const L2ResourcePage: FC<ResourcePageData> = ({
  data: {
    profile,
    contentstackL2ResourcePage,
    allContentstackL2ResourcePage,
    contentstackHeader,
    contentstackTranslations,
    contentstackHeaderWithSplitter,
    contentstackExtendedHeader,
    contentstackNewFooter,
    contentstackFooterWithSplitter,
    contentstackInfoBar,
    allContentstackItemListingGrid: { nodes: itemListingGrids },
    allContentstackAssets: { nodes: jsonRTEImages },
  },
  pageContext: { uid, locale, seoTitle, brand, fullPath },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const { isMobile } = useContext(ScreenRecognitionContext);

  const onlyForSignedIn = process.env.GATSBY_PROTECTED_PAGES_ENABLED === 'true';

  const { isLoggedIn } = useAuthGuard(onlyForSignedIn);

  const isAuthPending = onlyForSignedIn && isLoggedIn === null;
  const isAccessDenied = onlyForSignedIn && !isLoggedIn;

  if (isAuthPending) return null;

  if (isAccessDenied) {
    return <LoginRegisterBlocks variant={PAGE_ONLY_FOR_SIGNED_IN} />;
  }

  const t = mapContentstackTranslations(contentstackTranslations);

  const {
    seo,
    pageHeader,
    fields,
    body,
    categories,
    openGraph,
    page_additional_data,
    disabledLayoutBackground,
  } = contentstackL2ResourcePage;

  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );
  const mainImagePath =
    pageHeader.imageElement[0]?.image &&
    'url' in pageHeader.imageElement[0].image
      ? pageHeader.imageElement[0].image.url
      : undefined;

  const showAdditionalInformation = !!(
    page_additional_data.initial_publish_date || page_additional_data.duration
  );

  const externalData: BodyRenderExternalData = {
    locale,
    itemListingGrids,
    jsonRTEImages,
  };

  const relatedPages = allContentstackL2ResourcePage?.nodes || [];

  const mockItemListingCarousel = createItemListingCarouselMock(
    isMobile,
    t,
    PAGE_TYPE.RESOURCES
  );

  mockItemListingCarousel.items[0].items = relatedPages.map(
    mapPageDataToCarouselItem
  );

  const relatedResourcesMappedData = itemListingMapper(
    mockItemListingCarousel,
    uid
  ) as ItemListingCarouselProps;

  return (
    <Layout
      {...{
        openGraph,
        mainImagePath,
        seo,
        disabledLayoutBackground,
        extendedHeaderBasic: contentstackExtendedHeader,
        headerBasic: contentstackHeader,
        translations: t,
        headerWithSplitter: contentstackHeaderWithSplitter,
        headerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        footerBasic: contentstackNewFooter,
        footerWithSplitter: contentstackFooterWithSplitter,
        footerSplitterAdditionalData: {
          externalData,
          currentPageUid: uid,
        },
        pageUid: uid,
        locale,
        brand,
        pageHeaderProps: { pageHeader, fields },
        className: [
          BASE_CLASS,
          `${BASE_CLASS}--resource`,
          ...categoryClasses,
        ].join(' '),
        infoBar: {
          ...contentstackInfoBar,
        },
        fullPath,
        profile,
      }}
    >
      {showAdditionalInformation && (
        <div className="cds-section cds-page-block cds-section--default-mode showAdditionalInformation">
          <div className="cds-container cds-container--max-grid cds-page-block__content">
            <div className="date">
              {page_additional_data.initial_publish_date}
            </div>
            <div className="time">
              {`${page_additional_data.duration} ${
                t?.card?.timeUnit
              } ${getConsumptionTypeLabel(
                page_additional_data.content_type as ConsumptionType,
                t
              )}`}
            </div>
          </div>
        </div>
      )}

      <BodyRender
        items={body}
        externalData={externalData}
        currentPageUid={uid}
      />
      {relatedResourcesMappedData &&
        relatedResourcesMappedData.items.length > 0 && (
          <ItemListing {...relatedResourcesMappedData} />
        )}
    </Layout>
  );
};

export const query = graphql`
  query L2ResourcePage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
    $categoryUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackL2ResourcePage(id: { eq: $id }) {
      uid
      updated_at
      publish_details {
        time
      }
      fields {
        ...PageFieldsFragment
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      disabledLayoutBackground: disabled_layout_background
      pageHeader: page_header {
        ...PageHeaderFragment
      }
      body {
        ...BodyFragment
      }
      categories {
        title
      }
      page_additional_data {
        duration
        initial_publish_date
        content_type
      }
    }
    allContentstackItemListingGrid {
      nodes {
        ...ItemListingGridFragment
      }
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData(layout: FULL_WIDTH, quality: 80)
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackExtendedHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...ExtendedHeaderBasicFragment
    }
    contentstackTranslations(publish_details: { locale: { eq: $locale } }) {
      ...Translations
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackNewFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...NewFooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }

    allContentstackL2ResourcePage(
      filter: { categories: { elemMatch: { uid: { in: $categoryUids } } } }
      sort: {
        fields: [page_additional_data___initial_publish_date]
        order: DESC
      }
      limit: 12
    ) {
      nodes {
        uid
        updated_at
        fields {
          fullPath
        }
        pageHeader: page_header {
          headingBlock: heading_block {
            headline
            descriptionRte: description_rte
          }
          image_element {
            image {
              url
              gatsbyImageData(width: 550)
            }
            alt
            aspectRatio: aspect_ratio
            objectFit: object_fit
            objectPosition: object_position
            rounded
            caption
          }
        }
        publishDetails: publish_details {
          locale
        }
        categories {
          title
          uid
          use_in_lables
        }
        use_fallback_image
        listing_image {
          image {
            url
            gatsbyImageData(width: 550)
          }
          alt
          aspectRatio: aspect_ratio
          objectFit: object_fit
          objectPosition: object_position
          rounded
          caption
        }
        page_additional_data {
          duration
          initial_publish_date
          resource_link
          content_type
        }
        listing_data {
          description
          label {
            content
            sr_prefix
            variant
            size
          }
        }
      }
    }
  }
`;

export default withContentstackPreview(
  L2ResourcePage,
  addBrandPostfixConditionally(
    'l2_resource_page',
    process.env.GATSBY_CONTENTSTACK_BRAND_TAG
  )
);

export { Head } from '~/common/StructuredData';
